import type { DataTableColumn } from "@etudescarre/ui";
import { FilePdf as FilePdfIcon } from "@phosphor-icons/react";
import { useEffect } from "react";
import { useParams } from "react-router";
import { apiClient } from "@/lib/api/client";
import { formatDay } from "@/lib/utils/date";
import { Classeur, type FilterDef } from "../components/classeur";
import {
  useClasseurFilters,
  useClasseurState,
} from "../components/classeur/use-classeur-data";

type VisaItem = {
  id: string;
  numero: string | null;
  dateEnvoi: string | null;
  contractCode: string | null;
  commentCount: number;
  hasPdf: boolean;
};

const columns: DataTableColumn<VisaItem>[] = [
  { id: "numero", header: "N°", accessor: "numero" },
  {
    id: "dateEnvoi",
    header: "Date envoi",
    accessor: "dateEnvoi",
    cell: ({ value }) => (value ? formatDay(value as string) : "-"),
  },
  { id: "contractCode", header: "Contrat", accessor: "contractCode" },
  { id: "commentCount", header: "Documents", accessor: "commentCount" },
  {
    id: "pdf",
    header: "PDF",
    accessor: "hasPdf",
    cell: ({ value }) =>
      value ? (
        <FilePdfIcon size={18} className="text-red-600" />
      ) : (
        <span className="text-zinc-300">-</span>
      ),
  },
];

const filters: FilterDef[] = [];

export function ClasseurVisas() {
  const { yardId } = useParams<{ yardId: string }>();
  const { activeFilters, onFilterChange } = useClasseurFilters({ filters });
  const [state, setState] = useClasseurState<VisaItem>();

  useEffect(() => {
    async function fetchData() {
      setState((prev) => ({ ...prev, isLoading: true }));
      const { data } = await apiClient.GET("/api/yards/{yardId}/visas", {
        params: {
          path: { yardId: yardId ?? "" },
          query: { direction: "emitted" },
        },
      });
      if (data) {
        const items = data.data.items.map((item) => ({
          ...item,
          id: String(item.id),
        }));
        setState({ items, total: data.data.total, isLoading: false });
      } else {
        setState({ items: [], total: 0, isLoading: false });
      }
    }
    fetchData();
  }, [yardId, setState]);

  return (
    <Classeur
      title="Classeur visas entreprise générale"
      items={state.items}
      columns={columns}
      filters={filters}
      activeFilters={activeFilters}
      onFilterChange={onFilterChange}
      isLoading={state.isLoading}
      emptyMessage="Aucun visa"
      total={state.total}
    />
  );
}
